<template>
  <article
    class="product-card"
    :style="{ animationDelay: animationDelay }"
    @click="handleClick"
    tabindex="0"
    role="button"
    :aria-label="`ดูรายละเอียด ${product.Name_product || product.title}`"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <!-- Card Image -->
    <div class="card-image">
      <img
        :src="product.image"
        :alt="product.Name_product || product.title"
        loading="lazy"
      />
      <div class="image-overlay"></div>

      <!-- Features on Image -->
      <!--<div v-if="displayedFeatures.length > 0" class="features-on-image">
        <div
          v-for="(feature, idx) in displayedFeatures"
          :key="idx"
          class="feature-item"
        >
          <svg
            class="check-icon"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
          <span>{{ feature }}</span>
        </div>
      </div>-->
    </div>

    <!-- Card Content -->
    <div class="card-content">
      <div class="title-section">
        <p class="product-category">
          {{ product.Type_product || product.category }}
        </p>
        <h2 class="product-title">
          {{ product.Name_product || product.title }}
        </h2>
      </div>

      <!-- ✅ แสดงทั้ง 2 ฟิลด์ พร้อม spacing ที่เหมาะสม -->
      <div class="description">
        <p v-if="primaryDescription" class="primary-desc">
          {{ primaryDescription }}
        </p>
        <p v-if="secondaryDescription" class="secondary-desc">
          {{ secondaryDescription }}
        </p>
        <!-- Fallback -->
        <p
          v-if="!primaryDescription && !secondaryDescription"
          class="fallback-desc"
        >
          {{ fallbackDescription }}
        </p>
      </div>

      <button class="detail-button" type="button" @click.stop="handleClick">
        {{ buttonText }}
      </button>
    </div>
  </article>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed } from "vue";

// Props
const props = defineProps({
  product: {
    type: Object,
    required: true,
    validator: (value) => {
      return value.id && value.slug;
    },
  },
  maxFeatures: {
    type: Number,
    default: 4,
  },
  buttonText: {
    type: String,
    default: "ລາຍລະອຽດເພີ່ມເຕີມ",
  },
  animationDelay: {
    type: String,
    default: "0s",
  },
});

// Emits
const emit = defineEmits(["click"]);

// Computed Properties

/**
 * Get features list from product
 */
/*const displayedFeatures = computed(() => {
  if (!props.product.features) return [];

  const features = props.product.features;
  let featuresList = [];

  if (Array.isArray(features) && features.length > 0) {
    if (typeof features[0] === "object") {
      featuresList = features
        .map((f) => f.title || f.description || "")
        .filter(Boolean);
    } else {
      featuresList = features.filter(Boolean);
    }
  }

  return featuresList.slice(0, props.maxFeatures);
});*/

/**
 * Get primary description (title_primary)
 */
const primaryDescription = computed(() => {
  const text = props.product.title_primary || "";
  if (!text) return "";

  // ตัดให้สั้นลง (max 60 characters)
  if (text.length > 60) {
    return text.substring(0, 60) + "...";
  }
  return text;
});

/**
 * Get secondary description (title_secondary)
 */
const secondaryDescription = computed(() => {
  const text = props.product.title_secondary || "";
  if (!text) return "";

  // ตัดให้สั้นลง (max 80 characters)
  if (text.length > 80) {
    return text.substring(0, 80) + "...";
  }
  return text;
});

/**
 * Fallback description (ถ้าไม่มีทั้ง 2 ฟิลด์)
 */
const fallbackDescription = computed(() => {
  const text = props.product.subtitle || props.product.description || "";
  if (!text) return "";

  if (text.length > 100) {
    return text.substring(0, 100) + "...";
  }
  return text;
});

// Methods
const handleClick = () => {
  emit("click", props.product);
};
</script>

<style scoped>
/* ============================================================================
   PRODUCT CARD COMPONENT (DUAL DESCRIPTION VERSION) - FIXED & OPTIMIZED
   ============================================================================ */

@import url("https://fonts.googleapis.com/css2?family=Noto+Sans+Lao:wght@300;400;500;600;700&display=swap");

/* CSS Variables */
.product-card {
  --p-red: #ea2127;
  --text-dark: #343d47;
  --text-muted: #344873;
  --white: #ffffff;
  --card-shadow: 0 0.625rem 0.9375rem -0.1875rem rgba(0, 0, 0, 0.08);
  --card-shadow-hover: 0 1.25rem 1.5625rem -0.3125rem rgba(0, 0, 0, 0.1);
  --spacing-xs: 0.375rem;
  --spacing-sm: 0.5rem;
  --spacing-md: 0.9375rem;
  --spacing-lg: 1.5rem;
  --radius-lg: 1.25rem;
  --radius-full: 3.125rem;
  --transition-fast: 0.2s ease;
  --transition-normal: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --transition-slow: 0.5s ease;
  --font-xs: 0.75rem;
  --font-sm: 0.85rem;
  --font-base: 0.95rem;
  --font-md: 1rem;
  --font-lg: 1.3rem;
}

/* Card Container */
.product-card {
  font-family: "Noto Sans Lao", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  background: var(--white);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--card-shadow);
  transition: transform var(--transition-normal),
    box-shadow var(--transition-normal); /* ✅ แยก transition */
  display: flex;
  flex-direction: column;
  cursor: pointer;
  height: 100%;
  will-change: transform, box-shadow;
  backface-visibility: hidden;
  animation: slideUp 0.2s ease-out;
  position: relative;
  transform: translateY(0);
}

.product-card:hover {
  transform: translateY(-0.75rem);
  box-shadow: var(--card-shadow-hover);
}

.product-card:active {
  transform: translateY(-0.375rem);
}

/* Card Image Section */
.card-image {
  position: relative;
  height: 12rem;
  overflow: hidden;
  border-top-left-radius: var(--radius-lg);
  border-top-right-radius: var(--radius-lg);
  background: linear-gradient(135deg, #ffffff 0%, #fff3f3 100%);
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--transition-slow);
  display: block;
}

.product-card:hover .card-image img {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 55%,
    rgba(50, 50, 50, 0.63) 100%
  );
  pointer-events: none;
}

/* Features on Image */
.features-on-image {
  position: absolute;
  bottom: var(--spacing-md);
  left: var(--spacing-md);
  right: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  z-index: 1;
}

.feature-item {
  color: var(--white);
  font-size: var(--font-sm);
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  line-height: 1.5;
  text-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.3);
}

.check-icon {
  width: 1rem;
  height: 1rem;
  color: #4ade80;
  flex-shrink: 0;
  margin-top: 0.125rem;
  filter: drop-shadow(0 0.125rem 0.25rem rgba(0, 0, 0, 0.3));
}

/* Card Content */
.card-content {
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.title-section {
  margin-bottom: 0.25rem;
}

.product-category {
  color: var(--p-red);
  font-size: var(--font-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
  display: block;
}

.product-title {
  font-size: var(--font-lg);
  font-weight: 700;
  color: var(--text-muted);
  line-height: 1.4;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

/* ✅ Description - แสดง 2 บรรทัด พร้อม spacing ที่แก้ไข */
.description {
  margin-top: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  flex-grow: 1;
}

.primary-desc {
  font-size: var(--font-base);
  font-weight: 500;
  color: var(--text-dark);
  line-height: 1.5;
  margin: 0;
}

.secondary-desc {
  font-size: var(--font-base);
  font-weight: 500;
  color: var(--text-dark);
  line-height: 1.5;
  margin: 0;
  margin-top: var(--spacing-xs); /* ✅ เพิ่ม spacing ระหว่างบรรทัด */
}

.fallback-desc {
  font-size: var(--font-base);
  color: var(--text-muted);
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Detail Button */
.detail-button {
  width: 100%;
  padding: 0.9rem;
  border-radius: var(--radius-full);
  border: 0.125rem solid var(--p-red);
  background: transparent;
  color: var(--p-red);
  font-weight: 700;
  font-size: var(--font-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-family: inherit;
  position: relative;
  overflow: hidden;
  min-height: 2.75rem;
}

.product-card:hover .detail-button {
  color: var(--white);
  box-shadow: 0 0.25rem 1rem rgba(234, 33, 39, 0.25);
  background: var(--p-red);
}

.detail-button:active {
  transform: scale(0.98);
}

.detail-button:focus-visible {
  outline: 0.1875rem solid var(--p-red);
  outline-offset: 0.125rem;
}

/* Animation */
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(1.875rem);
  }
  to {
    opacity: 1;
    transform: translateY(0); /* ✅ ให้จบที่ 0 */
  }
}

/* Responsive */
@media (max-width: 64rem) {
  .card-image {
    height: 13.75rem;
  }
}

@media (max-width: 48rem) {
  .card-image {
    height: 11rem;
  }
  .features-on-image {
    bottom: 0.75rem;
    left: 0.75rem;
    right: 0.75rem;
  }
  .feature-item {
    font-size: 0.8125rem;
  }
  .card-content {
    padding: 1.25rem;
  }
  .product-title {
    font-size: 1.125rem;
  }
  .primary-desc {
    font-size: 0.875rem;
  }
  .secondary-desc {
    font-size: 0.875rem;
  }
  .description {
    margin-bottom: 1.25rem;
  }
  .detail-button {
    padding: 0.875rem 1rem;
    font-size: 0.9375rem;
  }
}

@media (max-width: 30rem) {
  .card-image {
    height: 11.25rem;
  }
  .product-title {
    font-size: 1rem;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  .product-card:hover {
    transform: none;
  }
  .card-image img {
    transition: none;
  }
}

@media (prefers-contrast: high) {
  .product-card {
    border: 0.125rem solid var(--text-dark);
  }
  .detail-button {
    border-width: 0.1875rem;
  }
}

.product-card:focus-visible {
  outline: 0.1875rem solid var(--p-red);
  outline-offset: 0.25rem;
}

/* Print Styles */
@media print {
  .product-card {
    page-break-inside: avoid;
    box-shadow: none;
    border: 0.0625rem solid #ddd;
  }
  .detail-button {
    display: none;
  }
}
</style>